import { API_CONFIG } from '../config/apiConfig';

// --- INTERFACES ---
export interface ExpenseData {
  id: string; 
  expenseOwnerType: string;
  expenseType: string;
  expenseSubType: string;
  expenseName: string;
  expenseAmount: string;
  expenseFrequency: string;
  totalExpenseAmount: string;
  totalFamilyExpenseAmount: string;
  status?: string;
  familyMemberId?: string;
  jogetId?: string;
}

export interface ExpensesApiResponse {
  status: string;
  data: ExpenseData[];
  pagination: {
    total: number;
    size: number;
    currentPage: number;
  };
}


// --- AUTHENTICATION HELPER ---
const getAuthToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('Authentication Error: JWT Token not found in localStorage.');
    return null;
  }
  return token;
};


// --- API FUNCTIONS ---

/**
 * Fetches all expense records for the logged-in user from your Node.js backend.
 */
export const fetchExpensesData = async (page = 1, size = 10): Promise<ExpensesApiResponse> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/expenses?page=${page}&size=${size}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(errorBody.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();

  } catch (error) {
    console.error('Error fetching expense data:', error);
    throw error;
  }
};


/**
 * Creates or updates an expense record via your Node.js backend.
 */
export const saveOrUpdateExpenseRecord = async (expense: ExpenseData): Promise<any> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  const payload = {
    id: expense.id || null, 
    status: expense.status || 'active',
    request_data: {
      expenseOwnerType: expense.expenseOwnerType,
      expenseType: expense.expenseType,
      expenseSubType: expense.expenseSubType,
      expenseName: expense.expenseName,
      expenseAmount: expense.expenseAmount,
      expenseFrequency: expense.expenseFrequency,
      totalExpenseAmount: expense.totalExpenseAmount,
      totalFamilyExpenseAmount: expense.totalFamilyExpenseAmount,
      familyMemberId: expense.familyMemberId,
    }
  };
  
  if (payload.request_data.familyMemberId === '' || payload.request_data.familyMemberId === undefined) {
      delete payload.request_data.familyMemberId;
  }

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/expenses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error saving expense record:`, error);
    throw error;
  }
};

/**
 * Soft deletes an expense record by its ID via your Node.js backend.
 */
export const deleteExpenseRecord = async (id: string): Promise<{ success: boolean; message: string }> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/expenses/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return { success: true, message: result.message };
  } catch (error: any) {
    console.error('Error deleting expense record:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to delete expense record.' 
    };
  }
};